import { z } from 'zod'

import { fileCompleteResponseSchema, validationErrors } from '.'
import { DefectNoticeTypeEnumSchema } from '../generated/residentApiSchemas'

export enum DefectClass {
  // OTHER = null, // 'Muu'
  REPAIR = 1, // 'Korjaus'
  HVAC_WORK = 2, // 'LVI-työ'
  MINOR = 3, // 'Pienkorjaus'
  ELECTRICAL = 4, // 'Sähkötyö'
  CLEANING = 5, // 'Siivous'
  PROCUREMENT = 6, // 'Hankinta'
  VANDALISM = 7, // 'Ilkivalta'
  MOISTURE = 8, // 'Kosteus'
  ADDITIONAL_WASTE = 9, // 'Lisäjäte'
  ADDITIONAL_CLEANING = 10, // 'Lisäsiivous'
  LOCKING = 11, // 'Lukitus'
  CONDITIONS = 12, // 'Olosuhteet'
  FIRE_DAMAGE = 13, // 'Palovahinko'
  BUILDING_AUTOMATION = 14, // 'Rakennusautomaatio'
  STRUCTURAL = 15, // 'Rakenteet'
  WINTER_MAINTENANCE_DAMAGE = 16, // 'Talvihoitovahinko'
  TELECOMMUNICATIONS = 17, // 'Tietoliikenneyhteydet'
  PESTS = 18, // 'Tuholaiset'
  WATER_DAMAGE = 19, // 'Vesivahinko'
}

export enum DefectLocation {
  CONTRACT_TARGET = 'CONTRACT_TARGET',
  COMMON_AREA = 'COMMON_AREA',
}

export const noticeReporterSchema = z.object({
  phoneNumber: z.string(),
  email: z.string().email(),
})

export const defectClassSchema = z.nativeEnum(DefectClass)

export const defectNoticeDefectClassesSchema = z.array(defectClassSchema)

export type DefectNoticeDefectClasses = z.infer<
  typeof defectNoticeDefectClassesSchema
>

const defectNoticeDescriptionFieldsSchema = z
  .object({
    title: z.string().max(100).nullable(),
    description: z.string().nullable(),
    fileUuids: z.array(z.string().uuid()),
  })
  .superRefine(({ title, description, fileUuids }, ctx) => {
    // At least one of the following fields can't be null
    if (
      title === null &&
      description === null &&
      (!fileUuids || fileUuids.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: validationErrors.title_description_or_files_required,
        path: ['description'],
      })
    }
  })

export const createDefectNoticeRequestSchema = z
  .object({
    contractUuid: z.string().uuid(),
    defectLocation: z.nativeEnum(DefectLocation),
    defectClassId: z.nativeEnum(DefectClass).nullable(),
    hasPetsInApartment: z.boolean(),
    masterKeyAccessAllowed: z.boolean(),
    noticeReporter: noticeReporterSchema.nullable(),
  })
  .and(defectNoticeDescriptionFieldsSchema)

export type CreateDefectNotice = z.infer<typeof createDefectNoticeRequestSchema>

export enum NoticeStatus {
  COMPLETED = 'completed',
  CREATED = 'created',
  IN_PROGRESS = 'in_progress',
}

export const noticeEventSchema = z.object({
  id: z.number(),
  eventType: z.string(),
  createdAt: z.string(),
  workFinishedAt: z.string().nullable(),
  workOrder: z
    .object({
      id: z.number(),
    })
    .optional()
    .nullable(),
  workNoteDescription: z.string().nullable().optional(),
})

export const defectNoticeListItemSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  defectClassId: z.nativeEnum(DefectClass).nullable(),
  defectLocation: z.nativeEnum(DefectLocation),
  latestEvent: noticeEventSchema,
  status: z.nativeEnum(NoticeStatus),
})

export const defectNoticeListSchema = z.array(defectNoticeListItemSchema)

export type DefectNoticeListItem = z.infer<typeof defectNoticeListItemSchema>

const fileCompleteResponseWithUrlSchema = fileCompleteResponseSchema.extend({
  url: z.string(),
})

export const defectNoticeDetailsSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  defectClassId: z.nativeEnum(DefectClass).nullable(),
  defectLocation: z.nativeEnum(DefectLocation),
  files: z.array(fileCompleteResponseWithUrlSchema).optional(),
  contractUuid: z.string().uuid(),
  hasPetsInApartment: z.boolean(),
  masterKeyAccessAllowed: z.boolean(),
  noticeReporter: z
    .object({
      phoneNumber: z.string(),
      email: z.string().email().nullable(),
      description: z.string().optional(),
    })
    .nullable(),
  status: z.nativeEnum(NoticeStatus),
  // A notice reported from the resident app can be changed to an observation in
  // Kenno. It stays visible to the resident, but is no longer editable.
  noticeType: DefectNoticeTypeEnumSchema,
  isTrackable: z.boolean(),
  events: z.array(noticeEventSchema).optional(),
})

export type DefectNoticeDetails = z.infer<typeof defectNoticeDetailsSchema>
export type DefectNoticeEvent = z.infer<typeof noticeEventSchema>
